/* eslint-disable max-lines */
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { color } from 'styles/colors'
import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import Spinner from 'components/Spinner'

import { ButtonContainer, PageTitle, screenSize, SinglePanePage } from 'styles/shared'
import Brand from 'helpers/brand'
import useActivePremiumAccount from 'hooks/useActivePremiumAccount'
import useActiveHehmAccount from 'hooks/useActiveHehmAccount'
import { useFeaturesContext } from 'hooks/FeaturesContext'
import usePaywallPremiumAcct from 'hooks/usePaywallPremiumAcct'
import usePaywallPlans from 'hooks/usePaywallPlans'
import { BillingData } from 'types/BillingData'
import ChangePlanModal from 'pages/paywall/ChangePlanModal'
import CancelModal from 'pages/paywall/CancelModal'
import PremiumFeaturesModal from 'pages/paywall/PremiumFeaturesModal'
import LabelValuePair from 'pages/paywall/LabelValuePair'
import { shortDateTime } from 'helpers/formatters'
import GA4 from 'helpers/ga4'
import Footer from 'components/Footer'

import ArrowIcon from '@mui/icons-material/TrendingFlat'
import FeatureCard from './FeatureCard'
import { useSessionContext } from 'hooks/SessionContext'
import useDealer from 'hooks/useDealer'
import PremiumAccountSummary from './AccountSummaryCards/PremiumAccountSummary'
import HEHMAccountSummary from './AccountSummaryCards/HEHMAccountSummary'
import HEHMCancelModal from './HEHMCancelModal'

const HEHM_FEATURE_CARD = {
  isNew: true,
  cardColors: {
    topLeftColor: color('hehmCardGradientTopLeft'),
    fiftyPercentColor: color('hehmCardGradientFiftyPercent'),
    bottomRightColor: color('hehmCardGradientBottomRight')
  },
  cardTitle: 'HVAC Performance Monitoring Kit',
  cardText: [
    'Click below to learn about a smarter, more proactive way of servicing your customers\' equipment with HVAC Performance Monitoring Kit! '
  ],
  mainAccentColor: color('upgradeButton'),
  buttonText: 'Learn more',
  imageSrc: Brand.hehmProductLogoPath(),
  imageAltText: 'HEHM system logo',
  imageHeight: 31
}

const DIAGNOSTICS_PREMIUM_FEATURE_CARD = {
  isNew: true,
  cardColors: {
    topLeftColor: color('premiumCardGradientTopLeft'),
    fiftyPercentColor: color('premiumCardGradientFiftyPercent'),
    bottomRightColor: color('premiumCardGradientBottomRight')
  },
  cardTitle: 'Diagnostics Premium',
  cardText: [
    [
      'Unlimited Access to Tech Assistant AI Chatbot in Technician App',
      'Extended Runtime History',
      'Maintenance Trends',
      'System Report Emailed to Customers',
      'Historical System Report Storage'
    ],
    'To access the full suite of Diagnostics features, upgrade to Premium. 30 day free trial available.'
  ],
  mainAccentColor: color('premiumAccent'),
  buttonText: 'Explore the features',
  imageSrc: Brand.premiumProductLogoPath(),
  imageAltText: 'Diagnostics Premium logo'
}

const PaywallDetails = () => {
  const { dealerId } = useParams()
  const navigate = useNavigate()
  const { session } = useSessionContext()

  const { activeAccount, isActiveAccountLoaded, planChange, isPaywallPlanChangeLoaded } = useActivePremiumAccount(dealerId)
  const { activeHehmAccount, isActiveHehmAccountLoaded, hehmQuantity, isHehmQuantityLoaded } = useActiveHehmAccount(dealerId)
  const { premiumAccount, isPremiumAcctLoaded } = usePaywallPremiumAcct(dealerId)
  const { plans, yearlyPlanNo, monthlyPlanNo, monthlyHEHMPlanNo, arePaywallPlansLoaded } = usePaywallPlans(dealerId)

  const { dealer: { channel }, isDealerLoaded } = useDealer(dealerId)
  const { enabledFeatures, isEnabledFeaturesLoaded } = useFeaturesContext()

  const [displayChangePlanModal, setDisplayChangePlanModal] = useState(false)
  const [displayPremiumFeatures, setDisplayPremiumFeatures] = useState(false)
  const [displayCancelModal, setDisplayCancelModal] = useState(false)
  const [displayHehmCancelModal, setDisplayHehmCancelModal] = useState(false)
  const [activeBillingData, setActiveBillingData] = useState<BillingData>()
  const [activeHehmBillingData, setActiveHehmBillingData] = useState<BillingData>()
  const [planType, setPlanType] = useState<string>()
  const [currentPlan, setCurrentPlan] = useState(yearlyPlanNo)
  const [planIsDeactivated, setPlanIsDeactivated] = useState(false)
  const [planIsTrial, setPlanIsTrial] = useState(false)
  const [effectiveDate, setEffectiveDate] = useState<string>()

  const inactiveHehmAccount = session?.hehmPlan?.hehm_active === 'hehm_inactive' && session?.hehmPlan.hehm_info !== null

  useEffect(() => {
    if (isActiveAccountLoaded && arePaywallPlansLoaded && isPaywallPlanChangeLoaded &&
      isPaywallPlanChangeLoaded && isPremiumAcctLoaded && isActiveHehmAccountLoaded) {
      if (activeAccount) {
        setActiveBillingData(activeAccount.billingContact)

        if (activeAccount.planNo) {
          let planType = 'diag_yearly'
          for (let i = 0; i < plans.length; i++) {
            if (plans[i].no.toString() === activeAccount.planNo) {
              planType = plans[i].planId
              break
            }
          }

          if (planType === 'diag_yearly') {
            setPlanType('Yearly')
            setCurrentPlan(yearlyPlanNo)
            setEffectiveDate(premiumAccount?.effectiveDate)
          } else if (planType === 'diag_monthly') {
            setPlanType('Monthly')
            setCurrentPlan(monthlyPlanNo)
            setEffectiveDate(premiumAccount?.effectiveDate)
          } else if (planType === 'diag_trial' || planType === 'diag_monthly_trial' || planType === 'diag_yearly_trial') {
            setPlanType('Trial')
            setCurrentPlan(0)
            setPlanIsTrial(true)
            setEffectiveDate(premiumAccount?.effectiveDate)
          }
        }
      } else {
        setPlanType('Standard')
        setPlanIsDeactivated(true)
      }

      if (activeHehmAccount) {
        setActiveHehmBillingData(activeHehmAccount.billingContact)
      }
    }
  }, [isActiveAccountLoaded, activeAccount, arePaywallPlansLoaded, isPaywallPlanChangeLoaded,
    isPremiumAcctLoaded, plans, yearlyPlanNo, monthlyPlanNo, premiumAccount, isActiveHehmAccountLoaded, activeHehmAccount])

  const onCloseModalCallback = (reload: boolean) => {
    setDisplayChangePlanModal(false)
    setDisplayCancelModal(false)
    setDisplayHehmCancelModal(false)
    setDisplayPremiumFeatures(false)
    if (reload) {
      navigate(-1)
    }
  }

  const sendGA4Event = (label: string, featureType: string) => {
    GA4.sendEvent({
      action: `Click ${featureType} Account Settings Option`,
      category: 'Account Settings',
      label: `Button: ${label}`
    })
  }

  const onChangePlanTypeClick = () => {
    sendGA4Event('Change Plan', 'Premium')
    setDisplayChangePlanModal(!displayChangePlanModal)
  }

  const onCancelPremiumPlanRenewalClick = () => {
    sendGA4Event('Cancel Plan', 'Premium')
    setDisplayCancelModal(!displayCancelModal)
  }

  const onCancelHEHMPlanRenewalClick = () => {
    sendGA4Event('Cancel Plan', 'PMK')
    setDisplayHehmCancelModal(!displayHehmCancelModal)
  }

  const onChangePremiumCreditCardInfoClick = () => {
    sendGA4Event('Change Credit Card', 'Premium')
    navigate(`/paywall/account/${dealerId}/${currentPlan}/${planChange ? planChange.targetPlanId : planType === 'Yearly' ? 'diag_yearly' : 'diag_monthly'}`)
  }

  const onChangeHEHMCreditCardInfoClick = () => {
    sendGA4Event('Change PMK Credit Card', 'PMK')
    navigate(`/ehmkt/account/${dealerId}/${monthlyHEHMPlanNo}/hehm_monthly`)
  }

  const onChangeHEHMBillingInfoClick = () => {
    sendGA4Event('Change Billing Info', 'PMK')
    navigate(`/ehmkt/account/${dealerId}/${monthlyHEHMPlanNo}/hehm_monthly`)
  }

  const onPremiumFeaturesClick = () => {
    setDisplayPremiumFeatures(true)
  }

  const onHehmLearnMoreClick = () => {
    GA4.sendEvent({
      action: 'Learn More PMK Features',
      category: 'Account Settings'
    })

    navigate(`/ehmkt/options/${dealerId}`)
  }

  const onPremiumLearnMoreClick = () => {
    GA4.sendEvent({
      action: 'Explore New Premium Features',
      category: 'Account Settings'
    })
    navigate(`/paywall/options/${dealerId}`)
  }

  if (!isActiveAccountLoaded || !isPaywallPlanChangeLoaded || !isPremiumAcctLoaded ||
      !arePaywallPlansLoaded || !isDealerLoaded || !isActiveHehmAccountLoaded) {
    return <Spinner variant="page" />
  }

  return (
    <S.PageContainer>
      <S.Top>
        <Button decoration="link" onPress={() => navigate(-1)}>
          <S.ArrowIcon fontSize="large" />
        </Button>
        {premiumAccount ? (<S.PageTitle>Account Settings</S.PageTitle>) : (
          <S.ProductLogo
            src={Brand.productLogoPath()}
            alt={`${Brand.brandName()} logo`}
          />
        )}
        <div />
      </S.Top>
      <S.Divider />
      <S.ContentWrapper>
        <S.TwoPaneContainer>
          {(activeHehmAccount || inactiveHehmAccount) && (
            <S.LeftPane>
              <HEHMAccountSummary
                activeBillingData={activeHehmBillingData as BillingData}
                activeHehmAccount={activeHehmAccount !== undefined}
                hehmAccountUpdatedAt={activeHehmAccount?.updatedAt}
                hehmQuantity={hehmQuantity}
                isHehmQuantityLoaded={isHehmQuantityLoaded}
                inactiveHehmAccount={inactiveHehmAccount}
                channel={channel}
                premiumAccount={premiumAccount}
                cancelPlanRenewalClickHandler={onCancelHEHMPlanRenewalClick}
                changeBillingInfoClickHandler={onChangeHEHMBillingInfoClick}
                changeCreditCardInfoClickHandler={onChangeHEHMCreditCardInfoClick}
              />
            </S.LeftPane>
          )}
          <S.LeftPane>
            <PremiumAccountSummary
              activeBillingData={activeBillingData as BillingData}
              activeHehmAccount={activeHehmAccount !== undefined}
              planChange={planChange}
              planIsDeactivated={planIsDeactivated}
              planIsTrial={planIsTrial}
              planType={planType || 'Standard'}
              premiumAccount={premiumAccount}
              cancelPlanRenewalClickHandler={onCancelPremiumPlanRenewalClick}
              changeCreditCardInfoClickHandler={onChangePremiumCreditCardInfoClick}
              changePlanTypeClickHandler={onChangePlanTypeClick}
              premiumFeaturesClickHandler={onPremiumFeaturesClick}
            />
          </S.LeftPane>
          {!premiumAccount && (
            <S.RightPane>
              <FeatureCard {...DIAGNOSTICS_PREMIUM_FEATURE_CARD} onPress={onPremiumLearnMoreClick} />
            </S.RightPane>
          )}
          {isEnabledFeaturesLoaded && enabledFeatures.includes('hehm') && (activeHehmAccount === undefined || activeHehmAccount === null) && !inactiveHehmAccount && (
            <S.RightPane>
              <FeatureCard {...HEHM_FEATURE_CARD} onPress={onHehmLearnMoreClick} />
            </S.RightPane>
          )}
        </S.TwoPaneContainer>
        <S.ButtonContainer>
          <S.LastUpdated>Last updated by {premiumAccount?.updatedBy} on {shortDateTime(premiumAccount?.updatedAt)}</S.LastUpdated>
          <Button decoration="positive" text="Close" onPress={() => navigate(-1)} styles={{ text: { textTransform: 'none' } }} />
        </S.ButtonContainer>
      </S.ContentWrapper>
      <S.FooterContainer>
        <Footer />
      </S.FooterContainer>
      {displayChangePlanModal &&
        <ChangePlanModal
          closeCallback={onCloseModalCallback}
          dealerId={Number(dealerId)}
          planChange={planChange}
          currentPlan={currentPlan}
          yearlyPlanNo={yearlyPlanNo}
        />}
      {displayCancelModal &&
        <CancelModal
          closeCallback={onCloseModalCallback}
          dealerId={Number(dealerId)}
          planChange={planChange}
          effectiveDate={effectiveDate}
          inTrial={planIsTrial}
        />}
      {displayHehmCancelModal &&
        <HEHMCancelModal
          channel={channel}
          dealerId={Number(dealerId)}
          closeCallback={onCloseModalCallback}
        />}
      {displayPremiumFeatures &&
        <PremiumFeaturesModal
          isDeactivated={planIsDeactivated}
          closeCallback={onCloseModalCallback}
        />}
    </S.PageContainer>
  )
}

export default PaywallDetails

const S = {
  ArrowIcon: styled(ArrowIcon)`
    transform: rotate(180deg);
    color: ${color('breadCrumbsTextHover')};
  `,
  ButtonContainer: styled(ButtonContainer)`
    border-top: none;
    margin-top: 0px;
  `,
  ContentWrapper: styled(SinglePanePage)`
    display: flex;
    flex-direction: column;
    max-width: fit-content;
    padding-top: 40px;
    @media ${screenSize.small} {
      padding: 0 20px;
      margin-top: 0px;
    }
  `,
  Divider: styled.hr`
    border: 0;
    height: 1px;
    background-color: ${color('border')};
    width: 100%;
    margin-top: 20px;
    margin-bottom: 40px;
  `,
  FooterContainer: styled.div`
    width: calc(100% - 60px);
  `,
  LabelValuePair: styled(LabelValuePair)`
    margin-top: 15px;
  `,
  LastUpdated: styled.i`
    font-size: 12px;
    width: 100%;
    align-self: center;
    color: ${color('premiumText')};
  `,
  PageContainer: styled.div`
    display: flex;
    flex: 1 1 auto;
    width: 100%;
    height: 100%;
    flex-direction: column;
    background-color: ${color('background')};
  `,
  PageTitle: styled(PageTitle)`
    margin-top: 10px;
    text-transform: none;
    text-wrap: wrap;
    font-weight: 600;
    font-size: 24px;

    @media ${screenSize.small} {
      display: block;
      color: ${color('primaryText')};
      font-size: 24px;
    }
  `,
  ProductLogo: styled.img`
    height: 50px;
    @media ${screenSize.small} {
      max-width: 290px;
    }
  `,
  TwoPaneContainer: styled.div`
    display: flex;
    flex-direction: row;
    gap: 20px;
    width: 100%;
    
    @media ${screenSize.medium} {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-template-rows: auto auto;
        & > div:nth-child(1) {
          grid-column: 1;
          grid-row: 1;
        }
        & > div:nth-child(2) {
          grid-column: 2;
          grid-row: 1;
        }
        
        & > div:nth-child(3) {
          grid-column: 1 / -1;
          grid-row: 2;
          max-width: 335px;
          justify-self: baseline;
        }
    }
    
    @media ${screenSize.small} {
      display: flex;
      flex-direction: column;
      margin-top: 20px;
    }
  `,
  LeftPane: styled.div`
    display: flex;
    flex-direction: column;
    max-width: 650px;
    flex: 1;
  `,
  RightPane: styled.div`
    display: flex;
    flex-direction: column;
    max-width: 335px;    
  `,
  Top: styled.div`
    display: flex;
    padding-top: 10px;
    padding-left: 20px;
  `
}
